/* SIYOU — Brut Pop global behavior (dependency-free, defer-loaded).
   1. Adds `bp-reveal-ready` to <html> — the only thing that arms the
      [data-bp-reveal] hidden state in brut-pop-base.css. If this file never
      runs (JS off, blocked, failed to load) the class is never added and
      content renders visible.
   2. Scroll reveal: IntersectionObserver adds .is-in to [data-bp-reveal];
      reveals everything immediately when IO is unsupported. Re-runs on
      shopify:section:load so the theme editor keeps working.
   3. Marquee pause: click delegation for .bp-marquee__pause toggles
      .is-paused on the closest .bp-marquee (animation-play-state), plus
      aria-pressed and a label swap via data-label-pause / data-label-play.
      Buttons with element children (icons) get the label via aria-label
      instead of textContent, so the swap never wipes out an icon node.
   4. Header teaser dropdowns: CSS owns open/close (:hover / :focus-within on
      .bp-hdr__item--drop); this only mirrors that state onto the parent
      link's aria-expanded and lets Escape close an open panel (.is-dismissed,
      cleared on the next hover or when focus leaves the item). */
(function () {
  'use strict';

  /* Arm the reveal's hidden state only from this file (`html.js` is already
     set by the inline head script, so it can't serve as a load fail-safe). */
  document.documentElement.classList.add('bp-reveal-ready');

  /* ---- scroll reveal ---- */
  var io = null;
  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
  }

  function revealInit(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var targets = scope.querySelectorAll('[data-bp-reveal]:not(.is-in)');
    for (var i = 0; i < targets.length; i++) {
      if (io) {
        io.observe(targets[i]);
      } else {
        targets[i].classList.add('is-in');
      }
    }
  }

  revealInit(document);
  document.addEventListener('shopify:section:load', function (event) {
    revealInit(event.target);
  });

  /* ---- marquee pause/play ---- */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var button = target.closest('.bp-marquee__pause');
    if (!button) return;
    var marquee = button.closest('.bp-marquee');
    if (!marquee) return;
    var paused = marquee.classList.toggle('is-paused');
    button.setAttribute('aria-pressed', paused ? 'true' : 'false');
    /* Label swap is opt-in via data-label-* attributes. Buttons without them
       (icon-only announcement pause, the ticker's constant mono "PAUSE")
       keep their content — state is conveyed by aria-pressed. */
    var label = paused
      ? button.getAttribute('data-label-play')
      : button.getAttribute('data-label-pause');
    if (label) {
      if (button.children.length) {
        button.setAttribute('aria-label', label);
      } else {
        button.textContent = label;
      }
    }
  });

  /* ---- header teaser dropdowns ---- */
  var DROP_ITEM = '.bp-hdr__item--drop';

  function dropLink(item) {
    return item.querySelector(':scope > .wt-header__nav-teaser__link');
  }

  function setDropExpanded(item, expanded) {
    var link = dropLink(item);
    if (link) link.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function leavesItem(event, item) {
    var next = event.relatedTarget;
    return !(next && next.nodeType === 1 && item.contains(next));
  }

  document.addEventListener('mouseover', function (event) {
    var item = event.target && event.target.closest ? event.target.closest(DROP_ITEM) : null;
    if (!item) return;
    if (item.classList.contains('is-dismissed')) {
      /* Re-entering after Escape re-arms the CSS hover state. */
      if (!event.relatedTarget || !item.contains(event.relatedTarget)) {
        item.classList.remove('is-dismissed');
      } else {
        return;
      }
    }
    setDropExpanded(item, true);
  });

  document.addEventListener('mouseout', function (event) {
    var item = event.target && event.target.closest ? event.target.closest(DROP_ITEM) : null;
    if (!item || !leavesItem(event, item)) return;
    if (item.contains(document.activeElement)) return; /* still open via focus-within */
    setDropExpanded(item, false);
  });

  document.addEventListener('focusin', function (event) {
    var item = event.target && event.target.closest ? event.target.closest(DROP_ITEM) : null;
    if (!item || item.classList.contains('is-dismissed')) return;
    setDropExpanded(item, true);
  });

  document.addEventListener('focusout', function (event) {
    var item = event.target && event.target.closest ? event.target.closest(DROP_ITEM) : null;
    if (!item || !leavesItem(event, item)) return;
    item.classList.remove('is-dismissed');
    if (item.matches(':hover')) return; /* still open via hover */
    setDropExpanded(item, false);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    var item = event.target && event.target.closest ? event.target.closest(DROP_ITEM) : null;
    if (!item) {
      /* WCAG 1.4.13: a panel opened by hover must also close on Escape while
         keyboard focus is elsewhere (usually <body>). Focus stays put. */
      var hovered = document.querySelectorAll(DROP_ITEM + ':hover:not(.is-dismissed)');
      if (!hovered.length) return;
      event.preventDefault();
      for (var i = 0; i < hovered.length; i++) {
        hovered[i].classList.add('is-dismissed');
        setDropExpanded(hovered[i], false);
      }
      return;
    }
    if (item.classList.contains('is-dismissed')) return;
    var link = dropLink(item);
    if (!link || link.getAttribute('aria-expanded') !== 'true') return;
    event.preventDefault();
    item.classList.add('is-dismissed');
    setDropExpanded(item, false);
    if (document.activeElement !== link) link.focus();
  });
})();
